/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2019-2021 by the xcube development team and contributors.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies
 * of the Software, and to permit persons to whom the Software is furnished to do
 * so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import {
    blue,
    lightBlue,
    green,
    grey,
    purple,
    red,
    pink,
    yellow,
    orange,
    cyan,
    indigo,
    deepPurple,
    lime,
} from '@material-ui/core/colors';
import { Color, PaletteType } from '@material-ui/core';
import { PaletteColorOptions } from '@material-ui/core/styles/createPalette';

import { LanguageDictionary } from './util/lang';
import { getQueryParameterByName } from './util/qparam';
import lang from './resources/lang.json';

const version = '0.4.5';

const defaultApiServerUrl = process.env.REACT_APP_XCUBE_API;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface Branding {
    appBarTitle: string;
    windowTitle: string;
    defaultApiServerId: string;
    defaultApiServerName: string;
    defaultApiServerUrl: string;
    themeName: 'dark' | 'light';
    primaryColor: PaletteColorOptions;
    secondaryColor: PaletteColorOptions;
    headerBackgroundColor?: string;
    logoPath: any;
    logoWidth: number;
    baseMapUrl?: string;
    defaultAgg?: 'median' | 'mean';
    polygonFillOpacity?: number;
}

const brandings: { [name: string]: Branding } = {
    'default': {
        appBarTitle: 'xcube Viewer',
        windowTitle: 'xcube Viewer',
        defaultApiServerId: 'local',
        defaultApiServerName: 'Local Server',
        defaultApiServerUrl: defaultApiServerUrl || 'http://localhost:8080',
        themeName: 'dark',
        primaryColor: blue,
        secondaryColor: pink,
        headerBackgroundColor: undefined,
        logoPath: require('./resources/default/logo.png').default,
        logoWidth: 32,
        baseMapUrl: 'https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        defaultAgg: 'mean',
    },
    'esdl': {
        appBarTitle: 'ESDL Viewer',
        windowTitle: 'ESDL Viewer',
        defaultApiServerId: 'esdl',
        defaultApiServerName: 'ESDL Server',
        defaultApiServerUrl: 'https://xcube.earthsystemdatalab.net',
        themeName: 'light',
        primaryColor: grey,
        secondaryColor: pink,
        headerBackgroundColor: grey['50'],
        logoPath: require('./resources/esdl/logo.png').default,
        logoWidth: 64,
        baseMapUrl: 'http://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        defaultAgg: 'mean',
    },
    'eodatabee': {
        appBarTitle: 'Demo Viewer',
        windowTitle: 'EODataBee Demo Viewer',
        defaultApiServerId: 'eodatabee',
        defaultApiServerName: 'EODataBee Server',
        defaultApiServerUrl: defaultApiServerUrl || 'https://xcube2.dcs4cop.eu/dcs4cop-dev/api/latest',
        themeName: 'dark',
        primaryColor: {
            light: '#ffd77c',
            main: '#ffc942',
            dark: '#b78c2f',
            contrastText: '#fff',
        },
        secondaryColor: pink,
        headerBackgroundColor: undefined,
        logoPath: require('./resources/eodatabee/logo.png').default,
        logoWidth: 150,
        baseMapUrl: 'http://a.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
        defaultAgg: 'mean',
    },
    'cyanoalert': {
        appBarTitle: '',
        windowTitle: 'CyanoAlert Viewer',
        defaultApiServerId: 'cyanoalert',
        defaultApiServerName: 'CyanoAlert Server',
        defaultApiServerUrl: defaultApiServerUrl || 'https://cyanoalert.brockmann-consult.com/api/latest',
        themeName: 'dark',
        primaryColor: {
            light: '#ceef64',
            main: '#9abc31',
            dark: '#688c00',
            contrastText: '#fff',
        },
        secondaryColor: deepPurple,
        headerBackgroundColor: undefined,
        logoPath: require('./resources/cyanoalert/logo.png').default,
        logoWidth: 120,
        baseMapUrl: 'https://tiles.wmflabs.org/bw-mapnik/{z}/{x}/{y}.png',
        defaultAgg: 'median',
        polygonFillOpacity: 0.025,
    },
    'cefas': {
        appBarTitle: 'WFD Viewer',
        windowTitle: 'cefas WFD Viewer',
        defaultApiServerId: 'cefas',
        defaultApiServerName: 'cefas Server',
        defaultApiServerUrl: defaultApiServerUrl || 'https://localhost:8181',
        themeName: 'dark',
        primaryColor: {
            light: '#ceef64',
            main: '#9abc31',
            dark: '#688c00',
            contrastText: '#fff',
        },
        secondaryColor: deepPurple,
        headerBackgroundColor: '#0094c9',
        logoPath: require('./resources/cefas/eodatabee-cefas.png').default,
        logoWidth: 200,
        baseMapUrl: 'https://a.tile.osm.org/{z}/{x}/{y}.png',
        defaultAgg: 'mean',
    },
    'eutro': {
        appBarTitle: 'Eutrophication Xcube Viewer',
        windowTitle: 'Eutro Viewer',
        defaultApiServerId: 'local',
        defaultApiServerName: 'Local Server',
        defaultApiServerUrl: defaultApiServerUrl || 'http://localhost:8080',
        themeName: 'dark',
        primaryColor: blue,
        secondaryColor: pink,
        headerBackgroundColor: undefined,
        logoPath: require('./resources/default/logo.png').default,
        logoWidth: 32,
        baseMapUrl: 'https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        defaultAgg: 'mean',
    },
    'plume': {
        appBarTitle: 'Marine plumes Xcube Viewer',
        windowTitle: 'Plume Viewer',
        defaultApiServerId: 'local',
        defaultApiServerName: 'Local Server',
        defaultApiServerUrl: defaultApiServerUrl || 'http://localhost:8080',
        themeName: 'dark',
        primaryColor: blue,
        secondaryColor: pink,
        headerBackgroundColor: undefined,
        logoPath: require('./resources/default/logo.png').default,
        logoWidth: 32,
        baseMapUrl: 'https://a.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
        defaultAgg: 'mean',
    },
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const brandingName = process.env.REACT_APP_BRANDING || 'default';
const branding = brandings[brandingName];

if (!branding) {
    throw new Error(`illegal branding "${brandingName}"!`);
}

if (branding.windowTitle) {
    document.title = branding.windowTitle;
}

export const I18N = new LanguageDictionary(lang);


interface TileAccess {
    param: string;
    token: string;
}


const defaultApiServer = {
    id: branding.defaultApiServerId,
    name: getQueryParameterByName(null, 'serverName', branding.defaultApiServerName)!,
    url: getQueryParameterByName(null, 'serverUrl', branding.defaultApiServerUrl)!,
};

const apiServers = [
    {...defaultApiServer},
];


export function getVersion() {
    return version;
}

export function getBrandingName() {
    return brandingName;
}

export function getBranding() {
    return branding;
}

export function getDefaultApiServer() {
    return defaultApiServer;
}

export function getApiServers() {
    return apiServers;
}

// Array of user place colors in stable order (see #153)
const userPlaceColorsArray: [string, Color][] = [
    ["yellow", yellow],
    ["red", red],
    ["pink", pink],
    ["lightBlue", lightBlue],
    ["green", green],
    ["orange", orange],
    ["lime", lime],
    ["purple", purple],
    ["indigo", indigo],
    ["cyan", cyan],
    ["deepPurple", deepPurple],
];

const userPlaceColors: { [name: string]: Color } = (() => {
    const obj: { [name: string]: Color } = {};
    userPlaceColorsArray.forEach(([name, color]) => {
        obj[name] = color;
    });
    return obj;
})();

export const userPlaceColorNames = userPlaceColorsArray.map(([name, _]) => name);


export function getStrokeShade(paletteType: PaletteType) {
    return paletteType === 'light' ? 800 : 400;
}

export function getUserPlaceColorName(index: number): string {
    return userPlaceColorNames[index % userPlaceColorNames.length];
}

export function getUserPlaceColor(colorName: string, paletteType: PaletteType): string {
    const shade = getStrokeShade(paletteType);
    return userPlaceColors[colorName][shade];
}

// See resources/maps.json
const tileAccess: { [name: string]: TileAccess } = {
    'Mapbox': {
        param: 'access_token',
        token: 'pk.eyJ1IjoiZm9ybWFuIiwiYSI6ImNrM2JranV0bDBtenczb2szZG84djh6bWUifQ.q0UKwf4CWt5fcQwIDwF8Bg'
    },
};


export function getTileAccess(groupName: string) {
    return tileAccess[groupName];
}
